import { merge } from 'lodash';
import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { useDispatch } from 'react-redux';

import { type ConfigurableView, type ViewField, ViewTypeName } from '@air/api/types';

import { BoardlessConfigViewStore } from '~/classes/BoardlessConfigViewStore';
import {
  type DefaultBoardlessConfigViewsParams,
  defaultGalleryViewId,
  defaultTableViewId,
} from '~/constants/configViews';
import { filterOutMissingViewFields } from '~/hooks/useBoardlessConfigView/filterOutMissingViewFields';
import { setSavedConfigurableViewAction } from '~/store/configViews/actions';
import { currentConfigurableViewSelector } from '~/store/configViews/selectors';
import { useAirDeepEqualSelector } from '~/utils/ReduxUtils';

type UseBoardlessConfigViewObserverParams = {
  defaultSort?: DefaultBoardlessConfigViewsParams['defaultSort'];
  galleryConfigOptionsData: ViewField[] | undefined;
  pageId: string;
  tableConfigOptionsData: ViewField[] | undefined;
};

/**
 * Shared hook that handles config view observation logic for both private and public boardless config views.
 * This hook:
 * 1. Marks default views as `isDefault` and saves them when they're active
 * 2. Filters out missing view fields from saved views and updates them
 */
export const useBoardlessConfigViewObserver = ({
  defaultSort,
  galleryConfigOptionsData,
  pageId,
  tableConfigOptionsData,
}: UseBoardlessConfigViewObserverParams) => {
  const dispatch = useDispatch();
  const configView = useAirDeepEqualSelector(currentConfigurableViewSelector);

  useEffect(() => {
    if (!configView) {
      return;
    }

    if (configView.id === defaultGalleryViewId(pageId) || configView.id === defaultTableViewId(pageId)) {
      const configViewCopy = merge({}, configView);
      configViewCopy.isDefault = true;
      BoardlessConfigViewStore.getInstance().saveViewForPage({ pageId, view: configViewCopy });
      dispatch(setSavedConfigurableViewAction({ configurableView: configViewCopy }));
    }
  }, [configView, dispatch, pageId]);

  useEffect(() => {
    if (galleryConfigOptionsData && tableConfigOptionsData) {
      const configViewsForPage = BoardlessConfigViewStore.getInstance().getSavedViewsForPage(pageId, defaultSort);

      const galleryConfigView = configViewsForPage.find((view) => view.viewType.name === ViewTypeName.gallery);
      const tableConfigView = configViewsForPage.find((view) => view.viewType.name === ViewTypeName.table);

      const updatedGalleryView = filterOutMissingViewFields({
        existingViewFields: galleryConfigOptionsData,
        configView: galleryConfigView,
      });

      const updatedTableView = filterOutMissingViewFields({
        existingViewFields: tableConfigOptionsData,
        configView: tableConfigView,
      });

      const updatedConfigViews = [updatedGalleryView, updatedTableView].filter(
        (view): view is ConfigurableView => view !== undefined,
      );

      if (!isEqual(updatedConfigViews, configViewsForPage)) {
        BoardlessConfigViewStore.getInstance().setSavedViewsForPage(pageId, updatedConfigViews);
      }
    }
  }, [defaultSort, galleryConfigOptionsData, pageId, tableConfigOptionsData]);
};
